import React from 'react';
import styles from './Grid.module.css';

export interface GridColumn<T = any> {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (row: T) => React.ReactNode;
  onCellClick?: (row: T) => void;
  className?: string;
  headerClassName?: string;
}

interface GridProps<T = any> {
  columns: GridColumn<T>[];
  rows: T[];
  rowKey?: (row: T) => string | number;
  onRowClick?: (row: T) => void;
  sortKey?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (key: string) => void;
  className?: string;
  headerClassName?: string;
  bodyClassName?: string;
  rowClassName?: string | ((row: T) => string);
  rowAlign?: 'center' | 'start' | 'stretch';
  bodyCellClassName?: string;
}

const INTERACTIVE_ROW_TARGET_SELECTOR = 'input, select, textarea, button, a, label, [role="combobox"], [role="listbox"], [data-grid-interactive="true"]';

const isInteractiveRowTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return Boolean(target.closest(INTERACTIVE_ROW_TARGET_SELECTOR));
};

const Grid = <T,>({
  columns,
  rows,
  rowKey,
  onRowClick,
  sortKey,
  sortDirection,
  onSort,
  className,
  headerClassName,
  bodyClassName,
  rowClassName,
  rowAlign = 'center',
  bodyCellClassName,
}: GridProps<T>) => {
  const handleHeaderClick = (col: GridColumn<T>) => {
    if (col.sortable && onSort) onSort(col.key);
  };

  const getRowKey = (row: T, idx: number) => {
    if (rowKey) return rowKey(row);
    // fallback to index if no key function provided
    return idx;
  };

  return (
    <div className={styles.grid + (className ? ` ${className}` : '')}>
      <div className={styles.header + (headerClassName ? ` ${headerClassName}` : '')}>
        {columns.map(col => (
          <div
            key={col.key}
            className={styles.headerCell + (col.sortable ? ` ${styles.sortable}` : '') + (col.headerClassName ? ` ${col.headerClassName}` : '')}
            onClick={() => handleHeaderClick(col)}
            role={col.sortable ? 'button' : undefined}
            aria-disabled={!col.sortable}
          >
            <span>{col.label}</span>
            {col.sortable && sortKey === col.key && (
              <span className={styles.sortIndicator}>{sortDirection === 'asc' ? ' ▲' : ' ▼'}</span>
            )}
          </div>
        ))}
      </div>

      <div className={styles.body + (bodyClassName ? ` ${bodyClassName}` : '')}>
        {rows.map((row, idx) => {
          const resolvedRowClassName = typeof rowClassName === 'function' ? rowClassName(row) : rowClassName;

          return (
          <div
            key={String(getRowKey(row, idx))}
            className={
              styles.row
              + (rowAlign === 'start' ? ` ${styles.rowAlignStart}` : '')
              + (rowAlign === 'stretch' ? ` ${styles.rowAlignStretch}` : '')
              + (resolvedRowClassName ? ` ${resolvedRowClassName}` : '')
              + (onRowClick ? ` ${styles.clickableRow}` : '')
            }
            onClick={(event) => {
              if (!onRowClick || isInteractiveRowTarget(event.target)) {
                return;
              }

              onRowClick(row);
            }}
            onMouseDown={(event) => {
              if (isInteractiveRowTarget(event.target)) {
                event.stopPropagation();
              }
            }}
          >
            {columns.map(col => (
              <div
                key={col.key}
                className={
                  styles.cell
                  + (bodyCellClassName ? ` ${styles.cellCustom}` : '')
                  + (bodyCellClassName ? ` ${bodyCellClassName}` : '')
                  + (col.className ? ` ${col.className}` : '')
                }
                onClick={col.onCellClick ? (e => { e.stopPropagation(); col.onCellClick && col.onCellClick(row); }) : undefined}
                role={col.onCellClick ? 'button' : undefined}
                tabIndex={col.onCellClick ? 0 : undefined}
                onKeyDown={col.onCellClick ? (e => {
                  if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); col.onCellClick && col.onCellClick(row); }
                }) : undefined}
              >
                {col.render ? col.render(row) : (row as any)[col.key]}
              </div>
            ))}
          </div>
        );
        })}
      </div>
    </div>
  );
};

export { Grid };
export default Grid;
